import EventDispatcher from './EventDispatcher';
import logging from './logging';
import {
    Camera,
    ColorManagement,
    LinearSRGBColorSpace,
    Material,
    MeshBasicMaterial,
    NeutralToneMapping,
    OrthographicCamera,
    PCFSoftShadowMap,
    PerspectiveCamera,
    Scene,
    SRGBColorSpace,
    WebGLRenderer,
} from 'three';
import { gammaCorrect } from './utils/gammaCorrect';

const log = logging('render');


export const highlights: Record<string, Material> = {
    selection: new MeshBasicMaterial({
            color: gammaCorrect(0x14a79d),
        //    opacity: 0.5,
        //    transparent: true,
        }),
    target: new MeshBasicMaterial({
            color: 0x000000,
            opacity: 0.25,
            transparent: true,
        }),

    error: new MeshBasicMaterial({
            color: 0xF91800,
            opacity: 0.25,
            transparent: true,
        }),
};

/**
 * A render pipeline can start and stop rendering or render a single frame. The
 * actual rendering is handled by a subclass implementation that adds rendering
 * capability, namely the RenderPipeline implementation below.
 *
 * If we ever need it, the pipeline is the appropriate thing to ask for an
 * image of whatever's on the current render target. This is because the still
 * needs to be grabbed just after rendering has completed, otherwise you might
 * end up with a blank still.
 */
export default abstract class RenderPipeline extends EventDispatcher {
    renderer: WebGLRenderer;
    scene: Scene;
    abstract get camera(): Camera;
    get canvas(): HTMLCanvasElement { return this.renderer?.domElement; }

    /**
     * When in the running state, the render pipeline constantly checking the
     * update flag on each frame. If the update flag is true then the pipeline
     * actually renders the frame, otherwise it does nothing and checks again
     * on the next frame. The running state can be changed with the start and
     * stop methods.
     */
    update = true;

    /**
     * A flag indicating whether the pipeline is currently running. When it is
     * running (after start() is called), it checks the update flag on each
     * frame and renders only when there has been an update since the last
     * render. When it is stopped (initially and after stop() is called), the
     * update flag is not even checked.
     */
    private running: boolean = false;

    constructor(renderer?: WebGLRenderer, scene?: Scene) {
        super();

        ColorManagement.enabled = true;

        if(renderer) {
            this.renderer = renderer;
        } else {
            this.renderer = new WebGLRenderer({
                "antialias": true,
                "alpha": true,
            });
            this.renderer.shadowMap.enabled = true;
            this.renderer.shadowMap.type = PCFSoftShadowMap;
        }
        this.renderer.autoClear = false;

        this.renderer.outputColorSpace = SRGBColorSpace;
        this.renderer.toneMapping = NeutralToneMapping;

        if(scene) {
            this.scene = scene;
        } else {
            this.scene = new Scene();
        }

        this.animate = this.animate.bind(this);
        this.resize = this.resize.bind(this);
    }

    /**
     * Begin rendering. Note that even while the pipeline is started, it still
     * checks the `update` property on every frame to decide whether or not to
     * render.
     */
    start(): void {
        this.running = true;
        window.addEventListener("resize", this.resize);
        window.dispatchEvent(new Event('resize'));
        window.requestAnimationFrame(this.animate);
    }

    /**
     * Stops the rendering loop so that it no longer checks the 
     */
    stop(): void {
        this.running = false;
        window.removeEventListener("resize", this.resize);
    }

    /*
     * This is a private function that checks the this.continue flag and then
     * recursively calls itself if we're still running.
     */
    private animate() {
        if(this.running) {
            window.requestAnimationFrame(this.animate);
            if(this.update) {
                this.trigger('before-render');

                // Render the base scene as normal
                this.camera.layers.mask = 0b1111;
                this.renderer.clear();
                this.renderer.render(this.scene, this.camera);

                // Render the selected objects (thing that is currently being
                // operated upon)
                this.camera.layers.mask = 0b0010;
                this.scene.overrideMaterial = highlights.selection;
                this.renderer.render(this.scene, this.camera);

                // Render the target objects (thing that will be affected)
                this.camera.layers.mask = 0b0100;
                this.scene.overrideMaterial = highlights.target;
                this.renderer.render(this.scene, this.camera);

                // Render the error objects (thing that has an error)
                this.camera.layers.mask = 0b1000;
                this.scene.overrideMaterial = highlights.error;
                this.renderer.render(this.scene, this.camera);


                this.scene.overrideMaterial = null;

                this.update = false;
                this.trigger('after-render');
            }
        }
    }

    /**
     * Implementation must handle window resize events with this method.
     */
    protected abstract resize(): void;
}

export class DummyRenderPipeline extends RenderPipeline {

    get camera(): Camera {
        throw new Error('Method not implemented.');
    }

    start() {
        // do nothing
    }

    stop() {
        // do nothing
    }

    protected resize(): void {
        // do nothing
    }
}

/**
 * A toggleable render pipeline that has either an orthographic or perspective
 * camera.
 */
export class ToggleRenderPipeline extends RenderPipeline {
    private ortho: OrthoRenderPipeline;
    private perspective: PerspectiveRenderPipeline;
    private state: "ortho" | "perspective" = "ortho";

    constructor(renderer?: WebGLRenderer, scene?: Scene) {
        super(renderer, scene);

        this.ortho = new OrthoRenderPipeline(this.renderer, this.scene);
        this.perspective = new PerspectiveRenderPipeline(this.renderer, this.scene);
    }

    /**
     * Toggles between orthographic and perspective views.
     * @param which a string either "ortho" or "perspective"
     */
    view(which: "ortho" | "perspective") {
        this.state = which;
        this.update = true;
    }

    get camera() {
        switch(this.state) {
            case "ortho":
                return this.ortho.camera;
            case "perspective":
                return this.perspective.camera;
            default:
                log.error(`Unsupported view specification '${this.state}'`);
        }
    }

    resize() {
        this.update = true;
        this.ortho.resize();
        this.perspective.resize();
    }

}

export class OrthoRenderPipeline extends RenderPipeline {

    private ortho: OrthographicCamera;

    constructor(renderer?: WebGLRenderer, scene?: Scene) {
        super(renderer, scene);

        this.ortho = new OrthographicCamera();
        this.ortho.near = 0.1;
        this.ortho.far = 1000;
        // Probably want to set this dynamically based on the dimensions of
        // the unit, but this is the midpoint along X (24" + 2*0.75" side
        // panels), halfway up the height of the unit, and the 120 units in Z
        // is arbitrary for the orthographic camera.
        this.ortho.position.set(12.75, 42, 120);

        this.scene.add(this.ortho);

    }

    get camera() {
        return this.ortho;
    }

    resize() {
        this.update = true;
        const
            width = this.canvas.clientWidth,
            height = this.canvas.clientHeight,
            aspect = width/height;

        // Sets the size without updating the CSS style with the size.
        // Effectively this changes the pixel dimensions of the output to
        // match the screen size of the canvas.
        this.renderer.setSize(width, height, false);

        // The 50 is approximate here, may want to scale it to the height
        // of the unit.
        this.ortho.left = -aspect*50;
        this.ortho.right = aspect*50;
        this.ortho.top = 50;
        this.ortho.bottom = -50;
        this.ortho.updateProjectionMatrix();
    }

}


export class PerspectiveRenderPipeline extends RenderPipeline {

    private perspective: PerspectiveCamera;

    constructor(renderer?: WebGLRenderer, scene?: Scene) {
        super(renderer, scene);

        this.perspective = new PerspectiveCamera(75, 1.0, 0.1, 1000);
        this.perspective.position.set(12.75, 42, 75);
        this.scene.add(this.perspective);
    }

    get camera() {
        return this.perspective;
    }

    resize() {
        this.update = true;
        const
            width = this.canvas.clientWidth,
            height = this.canvas.clientHeight,
            aspect = width/height;

        // Sets the size without updating the CSS style with the size.
        // Effectively this changes the pixel dimensions of the output to
        // match the screen size of the canvas.
        this.renderer.setSize(width, height, false);

        // Note that you still have to run these methods on the camera or
        // the output will be distorted:
        this.perspective.aspect = aspect;
        this.perspective.updateProjectionMatrix();
    }
}

